/* API client — calls backend or returns mock data based on VITE_MOCK_API. */

import type {
  TestChannelRequestsParams,
  TenantConfig,
  ProcessingRequest,
  PaginatedResponse,
  StatsFlow,
  StatsOverview,
  EntitySearchResult,
  CorrectionItem,
  AuditEntry,
  AuditFlowFilter,
  AuditListResponse,
  LockInfo,
  BscListResponse,
  BscPractice,
  BscData,
  BscCount,
  BscSuggestion,
  BscOutputListResponse,
  BscReopenResult,
  BscClearResult,
  BscStatsResponse,
  BscConfig,
  BscConfidencePreview,
  BscUploadDocumentResponse,
  BscProcessDocumentResponse,
  BscDocumentTagsResponse,
  BscDeleteDocumentResponse,
  DdtListResponse,
  DdtPractice,
  DdtListFilters,
  DdtCountResponse,
  DdtSuggestion,
  FornitoreSuggestion,
  ArticoloSuggestion,
  AmministratoreSuggestion,
  SelfServiceMagicLink,
  RinascenteInvoiceListResponse,
  RinascenteInvoice,
  RinascenteListFilters,
  RinascenteCountResponse,
  RinascenteSuggestion,
  RinascenteEmail,
  RinascenteOutputListResponse,
  RinascenteReopenResult,
  RinascenteClearResult,
  RinascenteStatsResponse,
  RinascenteConfig,
  RinascenteConfidencePreview,
  ConversationByRequest,
  CertifiableBuildingsResponse,
  MopDeliveryState,
  MopListResponse,
  MopOutputDetail,
  CertifyCallerPayload,
  CertificationTreeResponse,
  CertificationAssetsResponse,
} from "./types";
import {
  mockGetTenantConfig,
  mockListRequests,
  mockGetRequest,
  mockSearchEntities,
} from "./mock-data";

const USE_MOCK = import.meta.env.VITE_MOCK_API === "true";
const BASE = import.meta.env.VITE_API_BASE_URL || "";

export function listMopOutputs(filters: { status?: MopDeliveryState; q?: string; limit?: number; offset?: number }, signal?: AbortSignal): Promise<MopListResponse> {
  const qs = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) {
    if (value !== undefined && value !== "") qs.set(key, String(value));
  }
  return apiFetch(`/api/v1/mop?${qs}`, { signal });
}

export function getMopOutput(id: string, signal?: AbortSignal): Promise<MopOutputDetail> {
  return apiFetch(`/api/v1/mop/${encodeURIComponent(id)}`, { signal });
}

let _getToken: (() => string | null) | null = null;
let _getTenantId: (() => string | null) | null = null;

/** Register the auth token getter (called once from AuthProvider). */
export function setTokenGetter(fn: () => string | null): void {
  _getToken = fn;
}

/** Register the tenant ID getter for X-Tenant-ID header (platform_admin). */
export function setTenantIdGetter(fn: () => string | null): void {
  _getTenantId = fn;
}

function buildHeaders(extra?: Record<string, string>): Record<string, string> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...extra,
  };
  const token = _getToken?.();
  if (token) headers["Authorization"] = `Bearer ${token}`;
  const tenantId = _getTenantId?.();
  if (tenantId) headers["X-Tenant-ID"] = tenantId;
  return headers;
}

/** Estrae `detail` da un corpo di errore JSON (`{"detail": "..."}`, la forma
 *  standard di FastAPI/HTTPException). Stringa vuota se il corpo non è JSON o
 *  non ha un `detail` testuale — il chiamante decide il fallback. */
function extractErrorDetail(rawBody: string): string {
  try {
    const parsed = JSON.parse(rawBody) as { detail?: unknown };
    if (typeof parsed.detail === "string") return parsed.detail.trim();
  } catch {
    // Corpo non JSON: nessun dettaglio da estrarre.
  }
  return "";
}

export async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const headers = buildHeaders(init?.headers as Record<string, string>);
  const res = await fetch(`${BASE}${path}`, { ...init, headers });
  if (res.status === 401) {
    window.dispatchEvent(new CustomEvent("auth:unauthorized"));
    throw new Error("Unauthorized");
  }
  if (res.status === 403) {
    // Il `detail` del server VINCE sul messaggio generico.
    //
    // PERCHE' E' CAMBIATO
    //   Qui c'era `throw new Error("Non hai accesso a questo tenant")` per
    //   OGNI 403, scritto quando il solo 403 possibile era l'accesso
    //   cross-tenant. Non e' piu' vero: il 403 e' ora anche il rifiuto di una
    //   modifica al prompt dell'agente da parte di chi non e' platform_admin
    //   (`put_conversation_config`), e in quel caso il messaggio generico e'
    //   sia sbagliato — l'accesso al tenant c'e' — sia dannoso, perche'
    //   sostituisce l'unica spiegazione utile («modificabile solo da un
    //   platform_admin, il testo NON e' stato salvato, copialo prima di
    //   ricaricare») con una frase che manda a cercare il problema altrove.
    //   Sarebbe stato il difetto di prima — un errore invisibile — travestito
    //   da errore visibile.
    //
    //   Tutti i 403 del backend portano un `detail` leggibile (la guardia di
    //   ruolo manda «Permessi insufficienti — richiesto: ...»), quindi
    //   mostrarlo e' sempre almeno informativo come la frase fissa. Il
    //   fallback resta per un 403 senza corpo, per esempio da un proxy.
    const grezzo = await res.text().catch(() => "");
    throw new Error(extractErrorDetail(grezzo) || "Non hai accesso a questo tenant");
  }
  if (!res.ok) {
    // Stesso principio del 403 sopra, generalizzato: un 400 di validazione
    // (es. `flow` sconosciuto sul registro attività) porta un `detail`
    // leggibile dalla stessa HTTPException — va mostrato al posto del corpo
    // grezzo, non invece che oltre ad esso.
    const text = await res.text().catch(() => "");
    const dettaglio = extractErrorDetail(text);
    throw new Error(dettaglio || `API ${res.status}: ${text || res.statusText}`);
  }
  return res.json();
}

/** Like apiFetch but returns the raw Response (for status-code branching like lock 423). */
export async function apiFetchRaw(path: string, init?: RequestInit): Promise<Response> {
  const headers = buildHeaders(init?.headers as Record<string, string>);
  const res = await fetch(`${BASE}${path}`, { ...init, headers });
  if (res.status === 401) {
    window.dispatchEvent(new CustomEvent("auth:unauthorized"));
    throw new Error("Unauthorized");
  }
  return res;
}

// Simulate async delay for mocks
function delay<T>(value: T, ms = 200): Promise<T> {
  return new Promise((resolve) => setTimeout(() => resolve(value), ms));
}

/* ─── Public API ─── */

export async function fetchTenantConfig(): Promise<TenantConfig> {
  if (USE_MOCK) return delay(mockGetTenantConfig());
  return apiFetch("/api/v1/tenant/config");
}

export async function fetchRequests(
  params: {
    origin?: "mop";
    status?: string;
    q?: string;
    id?: string;
    date_from?: string;
    date_to?: string;
    confidence_min?: number;
    confidence_max?: number;
    has_attachments?: boolean;
    priority?: string;
    sender?: string;
    impianto?: string;
    sub_type?: string;
    exclude_sub_type?: string;
    causale?: string;
    numero_riferimento?: string;
    discard_type?: string;
    had_corrections?: boolean;
    /** Famiglia ricavata dalla nota di scarto: è il drill-down delle statistiche. */
    discard_family?: string;
    /** Campo corretto a mano, e categoria di correzione: drill-down delle statistiche. */
    corrected_field?: string;
    correction_category?: string;
    sort?: string;
    page?: number;
    size?: number;
  } = {},
): Promise<PaginatedResponse<ProcessingRequest>> {
  if (USE_MOCK) return delay(mockListRequests(params.status, params.page, params.size));
  const qs = new URLSearchParams();
  if (params.status) qs.set("status", params.status);
  if (params.q) qs.set("q", params.q);
  if (params.id) qs.set("id", params.id);
  if (params.date_from) qs.set("date_from", params.date_from);
  if (params.date_to) qs.set("date_to", params.date_to);
  if (params.confidence_min != null) qs.set("confidence_min", String(params.confidence_min));
  if (params.confidence_max != null) qs.set("confidence_max", String(params.confidence_max));
  if (params.has_attachments != null) qs.set("has_attachments", String(params.has_attachments));
  if (params.priority) qs.set("priority", params.priority);
  if (params.sender) qs.set("sender", params.sender);
  if (params.impianto) qs.set("impianto", params.impianto);
  if (params.sub_type) qs.set("sub_type", params.sub_type);
  if (params.exclude_sub_type) qs.set("exclude_sub_type", params.exclude_sub_type);
  if (params.causale) qs.set("causale", params.causale);
  if (params.numero_riferimento) qs.set("numero_riferimento", params.numero_riferimento);
  if (params.discard_type) qs.set("discard_type", params.discard_type);
  if (params.had_corrections != null) qs.set("had_corrections", String(params.had_corrections));
  if (params.discard_family) qs.set("discard_family", params.discard_family);
  if (params.corrected_field) qs.set("corrected_field", params.corrected_field);
  if (params.correction_category) qs.set("correction_category", params.correction_category);
  if (params.sort) qs.set("sort", params.sort);
  if (params.page) qs.set("page", String(params.page));
  if (params.size) qs.set("size", String(params.size));
  const query = qs.toString();
  return apiFetch(`${params.origin === "mop" ? "/api/v1/mop/requests" : "/api/v1/requests"}${query ? "?" + query : ""}`);
}

export async function fetchRequestCount(
  opts?: { status?: string; date_from?: string; date_to?: string; date_field?: "created_at" | "updated_at"; confidence_min?: number; confidence_max?: number; sub_type?: string; exclude_sub_type?: string; discard_type?: string; had_corrections?: boolean; discard_family?: string; origin?: "conversation" | "mop" },
): Promise<{ count: number }> {
  if (USE_MOCK) return delay({ count: 2 });
  const qs = new URLSearchParams();
  // `origin` ha qui la STESSA semantica che in /requests: con 'conversation'
  // conta solo le pratiche dell'agente, senza lo passa e le ESCLUDE, perche'
  // altrimenti questi sono i contatori della coda RDI. Chi conta per la
  // dashboard Agente deve passarlo, o conta le RDI.
  if (opts?.origin === "conversation") qs.set("origin", opts.origin);
  if (opts?.status) qs.set("status", opts.status);
  if (opts?.date_from) qs.set("date_from", opts.date_from);
  if (opts?.date_to) qs.set("date_to", opts.date_to);
  if (opts?.date_field) qs.set("date_field", opts.date_field);
  if (opts?.confidence_min != null) qs.set("confidence_min", String(opts.confidence_min));
  if (opts?.confidence_max != null) qs.set("confidence_max", String(opts.confidence_max));
  if (opts?.sub_type) qs.set("sub_type", opts.sub_type);
  if (opts?.exclude_sub_type) qs.set("exclude_sub_type", opts.exclude_sub_type);
  if (opts?.discard_type) qs.set("discard_type", opts.discard_type);
  if (opts?.had_corrections != null) qs.set("had_corrections", String(opts.had_corrections));
  if (opts?.discard_family) qs.set("discard_family", opts.discard_family);
  const q = qs.toString();
  return apiFetch(`${opts?.origin === "mop" ? "/api/v1/mop/requests" : "/api/v1/requests"}/count${q ? "?" + q : ""}`);
}

export type SearchSuggestion = {
  id: string; subject: string; sender: string;
  motivo: string | null; numero_riferimento: string | null;
  impianto: string | null; status: string;
};

export async function fetchSearchSuggestions(q: string, origin?: "mop"): Promise<SearchSuggestion[]> {
  if (USE_MOCK) return delay([]);
  if (q.length < 2) return [];
  return apiFetch(`${origin === "mop" ? "/api/v1/mop/requests" : "/api/v1/requests"}/suggest?q=${encodeURIComponent(q)}`);
}

export async function fetchRequest(id: string): Promise<ProcessingRequest> {
  if (USE_MOCK) {
    const r = mockGetRequest(id);
    if (!r) throw new Error("Request non trovata");
    return delay(r);
  }
  return apiFetch(`/api/v1/requests/${id}`);
}

export async function fetchSiblingRequests(id: string): Promise<import("./types").SiblingTicket[]> {
  if (USE_MOCK) return delay([]);
  return apiFetch(`/api/v1/requests/${id}/siblings`);
}

export async function approveRequest(
  id: string,
  note: string,
  overrides: RdiOperatorOverrides = {},
  forceMatches?: Record<string, RdiForceMatch> | null,
  certifyCaller?: CertifyCallerPayload | null,
): Promise<{ status: string; request_id: string }> {
  if (USE_MOCK) return delay({ status: "done", request_id: id });
  const payload: Record<string, unknown> = { note, ...overrides };
  if (forceMatches) payload.force_matches = forceMatches;
  // La certificazione del chiamante viaggia nello stesso payload
  // dell'approvazione: assente ⇒ nessuna certificazione (comportamento
  // storico, il campo non veniva mai inviato).
  if (certifyCaller) payload.certify_caller = certifyCaller;
  return apiFetch(`/api/v1/requests/${id}/approve`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

/* Elenco degli edifici certificabili per il chiamante di una pratica.
   404/403 non sono gestiti qui: `apiFetch` alza, il chiamante decide. Il ramo
   mock serve solo alla verifica visiva senza backend (convenzione del file). */
export async function fetchCertifiableBuildings(
  id: string,
): Promise<CertifiableBuildingsResponse> {
  if (USE_MOCK) {
    return delay({
      phone_e164: "+393331234567",
      already_certified: false,
      customers: [
        {
          codaz: "GRPCP",
          codcli_for: "C6351",
          ragsoc: "GEMMO S.P.A.",
          ticket_building: "E002755",
          ticket_buildings: ["E002755"],
          buildings: [
            { codice_edificio: "E002755", descrizione: "Poliambulatorio", indirizzo: "Via Roma 1", comune: "Padova" },
            { codice_edificio: "E002756", descrizione: "Magazzino", indirizzo: "Via Verdi 9", comune: "Padova" },
          ],
          truncated: false,
        },
      ],
    });
  }
  return apiFetch(`/api/v1/requests/${id}/certifiable-buildings`);
}

/* ── Albero della certificazione (run certificazione-albero) ──────────────
   Sostituisce `certifiable-buildings`, che resta in piedi finche' il frontend
   nuovo non e' in produzione. La ricerca sugli edifici sta **sul server**: un
   cliente reale ne ha fino a 644, l'elenco non si puo' servire tutto. */
export async function fetchCertificationTree(
  id: string,
  params: { q?: string; offset?: number; limit?: number } = {},
): Promise<CertificationTreeResponse> {
  if (USE_MOCK) {
    return delay({
      phone_e164: "+393331234567",
      customers: [
        {
          codaz: "GRPCP",
          codcli_for: "C6351",
          ragsoc: "GEMMO S.P.A.",
          ticket_buildings: ["E002755"],
          granted: { certified: false, all_buildings: false, buildings: [], assets: [] },
          buildings: [
            { codice_edificio: "E002755", descrizione: "Poliambulatorio", indirizzo: "Via Roma 1", comune: "Padova", assets_count: 3 },
            { codice_edificio: "E002756", descrizione: "Magazzino", indirizzo: "Via Verdi 9", comune: "Padova", assets_count: 1 },
          ],
          total_buildings: 61,
          truncated: true,
        },
      ],
    });
  }
  const qs = new URLSearchParams();
  if (params.q) qs.set("q", params.q);
  if (params.offset != null) qs.set("offset", String(params.offset));
  if (params.limit != null) qs.set("limit", String(params.limit));
  const suffix = qs.toString() ? `?${qs}` : "";
  return apiFetch(`/api/v1/requests/${id}/certification-tree${suffix}`);
}

/* Impianti di un edificio, caricati quando l'operatore apre la riga. Il
   massimo reale e' 76 per edificio, quindi la ricerca su questo livello resta
   locale: qui si scarica una volta sola. */
export async function fetchCertificationAssets(
  id: string,
  params: { codaz: string; codcli_for: string; edificio: string },
): Promise<CertificationAssetsResponse> {
  if (USE_MOCK) {
    return delay({
      assets: [
        { codice_impianto: "I000123", desc_tipo: "Caldaia", descrizione: "Centrale termica", desc_prest: "Manutenzione", potenza: "120 kW" },
        { codice_impianto: "I000124", desc_tipo: "Gruppo frigo", descrizione: null, desc_prest: "Manutenzione", potenza: "80 kW" },
      ],
      truncated: false,
    });
  }
  const qs = new URLSearchParams({
    codaz: params.codaz,
    codcli_for: params.codcli_for,
    edificio: params.edificio,
  });
  return apiFetch(`/api/v1/requests/${id}/certification-tree/assets?${qs}`);
}

export async function rejectRequest(
  id: string,
  body: { categories: string[]; note: string },
): Promise<{ status: string; request_id: string }> {
  if (USE_MOCK) return delay({ status: "discarded", request_id: id });
  return apiFetch(`/api/v1/requests/${id}/reject`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function reprocessRequest(
  id: string,
): Promise<{ status: string; request_id: string }> {
  if (USE_MOCK) return delay({ status: "received", request_id: id });
  return apiFetch(`/api/v1/requests/${id}/reprocess`, {
    method: "POST",
  });
}

export interface PreviewTicketAssociabile {
  idchiamata: number;
  refimpianto?: string;
  refedificio?: string;
  motivo: string;
  dataora: string;
  stato: number;
  similarity: number;
  similarity_method: string;
}

export interface PreviewConfidenceResponse {
  overall_confidence: number;
  entity_confidences: Record<string, number>;
  causale: string;
  ticket_associabili: PreviewTicketAssociabile[];
  client_ticket_id: string | null;
  new_impianto_pk?: string | null;
}

/** Ricalcola overall_confidence + ticketAssociabili simulando le correzioni
 *  proposte (read-only, niente persistenza). */
export async function previewConfidence(
  id: string,
  corrections: { entity_type: string; new_pk: string }[],
): Promise<PreviewConfidenceResponse> {
  if (USE_MOCK) {
    return delay({
      overall_confidence: 0.85,
      entity_confidences: {},
      causale: "apertura",
      ticket_associabili: [],
      client_ticket_id: null,
    });
  }
  return apiFetch(`/api/v1/requests/${id}/preview-confidence`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ corrections }),
  });
}

export interface GeneratedRule {
  id: string;
  entity_type: string;
  rule_text: string;
  keywords: string[];
}

export interface CorrectResponse {
  status: string;
  request_id: string;
  generated_rules: GeneratedRule[];
}

export interface RdiOperatorOverrides {
  motivo?: string;
  priorita?: string | null;
  nota_operatore?: string;
}

export interface RdiForceMatch {
  pk: string;
  fields?: Record<string, unknown>;
  operator_override?: boolean;
}

export async function correctRequest(
  id: string,
  corrections: CorrectionItem[],
  note: string,
  overrides: RdiOperatorOverrides = {},
): Promise<CorrectResponse> {
  if (USE_MOCK) return delay({ status: "reprocessing", request_id: id, generated_rules: [] });
  return apiFetch(`/api/v1/requests/${id}/correct`, {
    method: "POST",
    body: JSON.stringify({ corrections, note, ...overrides }),
  });
}

export async function updateRule(
  ruleId: string,
  updates: { rule_text?: string; active?: boolean; keywords?: string[] },
): Promise<{ status: string; id: string }> {
  return apiFetch(`/api/v1/rules/${ruleId}`, {
    method: "PATCH",
    body: JSON.stringify(updates),
  });
}

export async function deleteRule(
  ruleId: string,
): Promise<{ status: string; id: string }> {
  return apiFetch(`/api/v1/rules/${ruleId}`, { method: "DELETE" });
}

export async function searchEntities(
  entityType: string,
  q: string,
  limit = 10,
  parentPk?: string,
): Promise<EntitySearchResult[]> {
  if (USE_MOCK) return delay(mockSearchEntities(entityType, q));
  const qs = new URLSearchParams({ q, limit: String(limit) });
  if (parentPk) qs.set("parent_pk", parentPk);
  return apiFetch(`/api/v1/entities/${entityType}/search?${qs}`);
}

export async function fetchEntityByPk(
  entityType: string,
  pk: string,
): Promise<EntitySearchResult> {
  return apiFetch(`/api/v1/entities/${entityType}/${encodeURIComponent(pk)}`);
}

export async function fetchEntityChildren(
  parentType: string,
  parentPk: string,
  childType: string,
): Promise<EntitySearchResult[]> {
  if (USE_MOCK) return delay([]);
  return apiFetch(`/api/v1/entities/${parentType}/${parentPk}/children/${childType}`);
}

/** Statistiche di un flusso: contratto condiviso fra verticali.
 *
 * `threshold` simula una soglia diversa da quella configurata sul tenant, per
 * la proiezione dell'invio automatico. Omesso, usa quella del tenant.
 *
 * `dimension` sceglie su quale dimensione conta le correzioni il blocco
 * `automation`. Omesso, il server applica `overall`.
 *
 * `period` è un numero di giorni oppure `"start"` per "Dall'inizio": in quel
 * caso il client manda `since=start` e NON manda `days` — è il server a
 * risolvere la finestra dalla prima pratica del flusso (vedi
 * `docs/pipeline/statistiche-dall-inizio/plan.md`, D1).
 */
export async function fetchStatsOverview(
  flow: StatsFlow,
  period: number | "start",
  threshold?: number,
  segment?: string,
  dimension?: string,
): Promise<StatsOverview> {
  const params = new URLSearchParams({ flow });
  if (period === "start") params.set("since", "start");
  else params.set("days", String(period));
  if (threshold !== undefined) params.set("threshold", String(threshold));
  // `segment` omesso = default del flusso; "all" = tutti i segmenti insieme.
  if (segment !== undefined) params.set("segment", segment);
  // Non mandato quando assente: così il default resta quello del server.
  if (dimension) params.set("dimension", dimension);
  return apiFetch(`/api/v1/stats/overview?${params.toString()}`);
}

export async function healthCheck(): Promise<{ status: string }> {
  if (USE_MOCK) return delay({ status: "ok" });
  return apiFetch("/health");
}

/* ─── Review Flow: Lock, Revert, Audit ─── */

export async function lockRequest(id: string): Promise<{ status: number; data: LockInfo }> {
  if (USE_MOCK) return delay({ status: 200, data: { locked: true, locked_by: "me", operator_name: "Me" } });
  const res = await apiFetchRaw(`/api/v1/requests/${id}/lock`, { method: "POST" });
  const data: LockInfo = await res.json();
  return { status: res.status, data };
}

export async function unlockRequest(id: string): Promise<void> {
  if (USE_MOCK) return;
  await apiFetchRaw(`/api/v1/requests/${id}/lock`, { method: "DELETE" });
}

export async function revertRequest(
  id: string,
): Promise<{ status: string; request_id: string }> {
  if (USE_MOCK) return delay({ status: "pending_review", request_id: id });
  return apiFetch(`/api/v1/requests/${id}/revert`, { method: "POST" });
}

export async function reopenRequest(
  id: string,
): Promise<{ status: string; request_id: string }> {
  if (USE_MOCK) return delay({ status: "pending_review", request_id: id });
  return apiFetch(`/api/v1/requests/${id}/reopen`, { method: "POST" });
}

export async function fetchAuditLog(
  filters: {
    page?: number;
    size?: number;
    date_from?: string;
    date_to?: string;
    operator_id?: string;
    action?: string;
    request_id?: string;
    bsc_practice_id?: string;
    /** Assente = tutti i flussi (righe senza flusso comprese); "none" = solo
     *  le righe senza flusso. Un valore ignoto → 400 lato server. */
    flow?: AuditFlowFilter;
    /** "*" = tutti i tenant, permesso solo al platform_admin (403 per gli
     *  altri ruoli). Assente = il proprio tenant (comportamento odierno). */
    tenant_id?: string;
  } = {},
): Promise<AuditListResponse> {
  if (USE_MOCK) return delay({ items: [], total: 0, page: 1, size: 20, tenant_scope: "" });
  const qs = new URLSearchParams();
  if (filters.page) qs.set("page", String(filters.page));
  if (filters.size) qs.set("size", String(filters.size));
  if (filters.date_from) qs.set("date_from", filters.date_from);
  if (filters.date_to) qs.set("date_to", filters.date_to);
  if (filters.operator_id) qs.set("operator_id", filters.operator_id);
  if (filters.action) qs.set("action", filters.action);
  if (filters.request_id) qs.set("request_id", filters.request_id);
  if (filters.bsc_practice_id) qs.set("bsc_practice_id", filters.bsc_practice_id);
  if (filters.flow) qs.set("flow", filters.flow);
  if (filters.tenant_id) qs.set("tenant_id", filters.tenant_id);
  const q = qs.toString();
  return apiFetch(`/api/v1/audit${q ? "?" + q : ""}`);
}

export async function fetchAuditByRequest(id: string): Promise<AuditEntry[]> {
  if (USE_MOCK) return delay([]);
  return apiFetch(`/api/v1/audit/by-request/${id}`);
}

/** Cronologia attività operatore su una pratica BSC (pannello "Attività" nel
 *  dettaglio). Stessa fonte della pagina globale "Registro attività". */
export async function fetchBscActivity(practiceId: string): Promise<AuditEntry[]> {
  if (USE_MOCK) return delay([]);
  return apiFetch(`/api/v1/bsc/${practiceId}/activity`);
}

export async function fetchLessons(
  page = 1,
  size = 50,
): Promise<PaginatedResponse<AuditEntry>> {
  if (USE_MOCK) return delay({ items: [], total: 0, page, size });
  return apiFetch(`/api/v1/audit/lessons?page=${page}&size=${size}`);
}

export async function updateLesson(
  auditId: string,
  lessonLearned: string,
): Promise<{ status: string; id: string }> {
  return apiFetch(`/api/v1/audit/lessons/${auditId}`, {
    method: "PATCH",
    body: JSON.stringify({ lesson_learned: lessonLearned }),
  });
}

export async function deleteLesson(
  auditId: string,
): Promise<{ status: string; id: string }> {
  return apiFetch(`/api/v1/audit/lessons/${auditId}`, { method: "DELETE" });
}

/** Fire-and-forget unlock using navigator.sendBeacon (for beforeunload). */
export function unlockRequestBeacon(id: string): void {
  const token = _getToken?.();
  const tenantId = _getTenantId?.();
  const url = `${BASE}/api/v1/requests/${id}/lock`;
  // sendBeacon only supports POST — we'll use fetch keepalive with DELETE
  try {
    const headers: Record<string, string> = { "Content-Type": "application/json" };
    if (token) headers["Authorization"] = `Bearer ${token}`;
    if (tenantId) headers["X-Tenant-ID"] = tenantId;
    fetch(url, { method: "DELETE", headers, keepalive: true }).catch(() => {});
  } catch {
    // ignore — best effort
  }
}

/* ─── Queue & Blob API ─── */

import type {
  QueueMessagesResponse,
  DdtQueueMessagesResponse,
  BlobListResponse,
  QueueStatus,
} from "./types";

/** Coda output RDI (punto 4, 2026-07-14): elenco letto da DB
 * (processing_requests), paginato — non piu' la coda Azure live, cosi' gli
 * elementi gia' prelevati (picked_up_at) restano in lista invece di sparire. */
export async function fetchQueueMessages(page = 1, size = 20, status: QueueStatus = "pending"): Promise<QueueMessagesResponse> {
  if (USE_MOCK) {
    return delay({
      queue_name: "upsert-ticket", message_count: 0, messages: [], total: 0, page, size,
      counts: { all: 0, pending: 0, in_lease: 0, picked_up: 0 }, peek_incomplete: false,
    });
  }
  return apiFetch(`/api/v1/queue/messages?page=${page}&size=${size}&status=${status}`);
}

export async function deleteQueueMessage(messageId: string): Promise<{ status: string; deleted: string; reopened?: string[]; already_picked_up?: boolean }> {
  if (USE_MOCK) return delay({ status: "ok", deleted: messageId });
  return apiFetch(`/api/v1/queue/messages/${encodeURIComponent(messageId)}`, { method: "DELETE" });
}

export async function clearQueue(): Promise<{ status: string; cleared: boolean; reopened?: string[] }> {
  if (USE_MOCK) return delay({ status: "ok", cleared: true });
  return apiFetch("/api/v1/queue/messages", { method: "DELETE" });
}

/** Coda output DDT (Item 1, 2026-07-14) — stessa Azure Storage della coda RDI
 * sopra, ma coda separata (tenant.ddt_config.output_queue_name). Niente
 * `reopened`: a differenza della coda RDI, il payload upsert-ddt non porta
 * l'id della pratica, quindi cancellare un messaggio qui non riapre nulla.
 * Punto 4 (2026-07-14): elenco letto da DB (ddt_practices), paginato. */
export async function fetchDdtQueueMessages(page = 1, size = 20, status: QueueStatus = "pending"): Promise<DdtQueueMessagesResponse> {
  if (USE_MOCK) {
    return delay({
      queue_name: "upsert-ddt", message_count: 0, messages: [], total: 0, page, size,
      counts: { all: 0, pending: 0, in_lease: 0, picked_up: 0 }, peek_incomplete: false,
    });
  }
  return apiFetch(`/api/v1/queue/ddt/messages?page=${page}&size=${size}&status=${status}`);
}

export async function deleteDdtQueueMessage(messageId: string): Promise<{ status: string; deleted: string; already_picked_up?: boolean }> {
  if (USE_MOCK) return delay({ status: "ok", deleted: messageId });
  return apiFetch(`/api/v1/queue/ddt/messages/${encodeURIComponent(messageId)}`, { method: "DELETE" });
}

export async function clearDdtQueue(): Promise<{ status: string; cleared: boolean }> {
  if (USE_MOCK) return delay({ status: "ok", cleared: true });
  return apiFetch("/api/v1/queue/ddt/messages", { method: "DELETE" });
}

export async function fetchBlobs(page = 1, size = 20): Promise<BlobListResponse> {
  if (USE_MOCK) return delay({ container_name: "email-archive", groups: [], total_groups: 0, page, size });
  return apiFetch(`/api/v1/queue/blobs?page=${page}&size=${size}`);
}

export async function downloadBlobUrl(blobPath: string): Promise<{ url: string; contentType: string }> {
  const headers: Record<string, string> = {};
  const token = _getToken?.();
  if (token) headers["Authorization"] = `Bearer ${token}`;
  const tenantId = _getTenantId?.();
  if (tenantId) headers["X-Tenant-ID"] = tenantId;

  const res = await fetch(`${BASE}/api/v1/queue/blobs/${blobPath}`, { headers });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`Download blob fallito: ${res.status} ${text || res.statusText}`);
  }
  const blob = await res.blob();
  return { url: URL.createObjectURL(blob), contentType: blob.type };
}

export async function deleteBlob(blobPath: string): Promise<{ status: string; deleted: string }> {
  if (USE_MOCK) return delay({ status: "ok", deleted: blobPath });
  return apiFetch(`/api/v1/queue/blobs/${blobPath}`, { method: "DELETE" });
}

export async function deleteBlobGroup(mailPath: string): Promise<{ status: string; deleted_count: number; mail_path: string }> {
  if (USE_MOCK) return delay({ status: "ok", deleted_count: 0, mail_path: mailPath });
  return apiFetch(`/api/v1/queue/blobs?mail_path=${encodeURIComponent(mailPath)}`, { method: "DELETE" });
}

/* ─── Confidence config API (legacy tenant-level) ─── */

import type {
  ConfidenceConfig,
  ConfidencePreview,
  FlowSummary,
  FlowConfig,
  FlowConfidence,
} from "./types";

export async function fetchConfidenceConfig(): Promise<ConfidenceConfig> {
  return apiFetch("/api/v1/tenant/config/confidence");
}

export async function updateConfidenceConfig(
  body: ConfidenceConfig,
): Promise<{ status: string }> {
  return apiFetch("/api/v1/tenant/config/confidence", {
    method: "PUT",
    body: JSON.stringify(body),
  });
}

export async function fetchConfidencePreviewLegacy(
  threshold: number,
  periodDays = 30,
): Promise<ConfidencePreview> {
  return apiFetch(
    `/api/v1/tenant/config/confidence/preview?threshold=${threshold}&period=${periodDays}`,
  );
}

/* ─── Flows API (virtual flows) ─── */

export async function fetchFlows(): Promise<{ flows: FlowSummary[] }> {
  return apiFetch("/api/v1/flows");
}

export async function fetchFlowConfig(flowId: string): Promise<FlowConfig> {
  return apiFetch(`/api/v1/flows/${flowId}/config`);
}

export async function updateFlowConfidence(
  flowId: string,
  body: FlowConfidence,
): Promise<{ status: string; flow_id: string }> {
  return apiFetch(`/api/v1/flows/${flowId}/config/confidence`, {
    method: "PUT",
    body: JSON.stringify(body),
  });
}

export async function updateFlowInput(
  flowId: string,
  mailboxes: { email: string; forwarder_addresses: string[] }[],
): Promise<{ status: string; flow_id: string }> {
  return apiFetch(`/api/v1/flows/${flowId}/config/input`, {
    method: "PUT",
    body: JSON.stringify({ mailboxes }),
  });
}

export async function updatePreAnalysisRules(
  flowId: string,
  rules: { sub_type: string; enabled: boolean }[],
): Promise<{ status: string; flow_id: string }> {
  return apiFetch(`/api/v1/flows/${flowId}/config/pre-analysis-rules`, {
    method: "PATCH",
    body: JSON.stringify({ rules }),
  });
}

export async function fetchConfidencePreview(
  flowId: string,
  threshold: number,
  period = 30,
): Promise<ConfidencePreview> {
  const qs = new URLSearchParams({
    threshold: String(threshold),
    period: String(period),
  });
  return apiFetch(`/api/v1/flows/${flowId}/config/confidence/preview?${qs}`);
}

/* ─── Admin API ─── */

import type {
  AdminTenant,
  TenantChannelsStatus,
  TenantChannelUpdate,
  AdminUser,
  CostSummary,
  CostByTenant,
  CostByType,
  CostByStep,
  CostDaily,
  CostGranularity,
  CostTimeseries,
  CostByRequestResponse,
  CostTopRequestsResponse,
} from "./types";

// Tenants
export async function adminListTenants(): Promise<AdminTenant[]> {
  return apiFetch("/api/v1/admin/tenants");
}

export async function adminCreateTenant(body: {
  tenant_id: string;
  name: string;
  config?: Record<string, unknown>;
}): Promise<{ tenant_id: string; status: string }> {
  return apiFetch("/api/v1/admin/tenants", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function adminDeleteTenant(
  tenantId: string,
): Promise<{ tenant_id: string; status: string }> {
  return apiFetch(`/api/v1/admin/tenants/${tenantId}`, { method: "DELETE" });
}

// Canali dell'agente conversazionale — stato sicuro (solo `enabled`, mai
// credenziali), riservato a platform_admin come il resto di questo file.
export async function adminGetTenantChannels(tenantId: string): Promise<TenantChannelsStatus> {
  return apiFetch(`/api/v1/admin/tenants/${tenantId}/channels`);
}

export async function adminSetTenantChannel(
  tenantId: string,
  channel: string,
  enabled: boolean,
): Promise<TenantChannelsStatus> {
  return apiFetch(`/api/v1/admin/tenants/${tenantId}/channels/${channel}`, {
    method: "PATCH",
    body: JSON.stringify({ enabled }),
  });
}

// Aggiorna credenziali (e/o `enabled`) di un canale. Ogni campo omesso da
// `update` resta invariato lato server — non inviare mai un campo con un
// valore vuoto/placeholder solo per "completare" il body: chi non deve
// cambiare va omesso del tutto.
export async function adminSetTenantChannelCredentials(
  tenantId: string,
  channel: string,
  update: TenantChannelUpdate,
): Promise<TenantChannelsStatus> {
  return apiFetch(`/api/v1/admin/tenants/${tenantId}/channels/${channel}`, {
    method: "PATCH",
    body: JSON.stringify(update),
  });
}

// Users
export async function adminListUsers(params?: {
  tenantId?: string;
  search?: string;
  role?: string;
  state?: string;
  limit?: number;
  offset?: number;
}): Promise<{ users: AdminUser[]; total: number }> {
  const qs = new URLSearchParams();
  if (params?.tenantId) qs.set("tenant_id", params.tenantId);
  if (params?.search) qs.set("search", params.search);
  if (params?.role) qs.set("role", params.role);
  if (params?.state) qs.set("state", params.state);
  if (params?.limit) qs.set("limit", String(params.limit));
  if (params?.offset) qs.set("offset", String(params.offset));
  const s = qs.toString();
  return apiFetch(`/api/v1/admin/users${s ? `?${s}` : ""}`);
}

export async function adminCreateUser(body: {
  email: string;
  first_name: string;
  last_name: string;
  tenant_id?: string;
  role?: string;
}): Promise<{ user_id: string; email: string; status: string }> {
  return apiFetch("/api/v1/admin/users", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function adminGetUser(
  userId: string,
): Promise<AdminUser> {
  return apiFetch(`/api/v1/admin/users/${userId}`);
}

export async function adminDeleteUser(
  userId: string,
): Promise<{ user_id: string; status: string }> {
  return apiFetch(`/api/v1/admin/users/${userId}`, { method: "DELETE" });
}

export async function adminDeactivateUser(
  userId: string,
): Promise<{ user_id: string; status: string }> {
  return apiFetch(`/api/v1/admin/users/${userId}/deactivate`, { method: "POST" });
}

export async function adminReactivateUser(
  userId: string,
): Promise<{ user_id: string; status: string }> {
  return apiFetch(`/api/v1/admin/users/${userId}/reactivate`, { method: "POST" });
}

export async function adminResendInvite(
  userId: string,
): Promise<{ user_id: string; status: string }> {
  return apiFetch(`/api/v1/admin/users/${userId}/resend-invite`, { method: "POST" });
}

export async function adminChangeRole(
  userId: string,
  role: string,
  append = false,
): Promise<{ user_id: string; role: string; status: string }> {
  return apiFetch(`/api/v1/admin/users/${userId}/roles`, {
    method: "POST",
    body: JSON.stringify({ role, append }),
  });
}

// Costs
/**
 * Finestra temporale per TUTTE le rotte costi: un preset (`period`) OPPURE un
 * intervallo di giorni di calendario (`dateFrom`+`dateTo`), mai entrambi — il
 * backend ignora `period` quando ci sono le due date, e mandarli insieme
 * renderebbe illeggibile la querystring nei log.
 *
 * I giorni sono "YYYY-MM-DD" nel fuso dichiarato dall'API (Europe/Rome), non
 * istanti: qui l'utente scrive giorni, quindi il frontend non fa alcuna
 * aritmetica di fuso su queste rotte.
 */
export interface CostWindow {
  /** Preset: "1d" | "7d" | "30d" | "90d" | "365d" | "1095d". Default "30d". */
  period?: string;
  /** Primo giorno della finestra, incluso ("YYYY-MM-DD"). */
  dateFrom?: string;
  /** Ultimo giorno della finestra, INCLUSO ("YYYY-MM-DD"). */
  dateTo?: string;
  tenantId?: string;
  flow?: string;
}

/** Chiavi di finestra che ogni rotta costi riecheggia in testa alla risposta. */
interface CostWindowEcho {
  period: string;
  date_from?: string;
  date_to?: string;
}

/**
 * Querystring della finestra. `date_from`/`date_to` sono un intervallo, non
 * due filtri: se ne arriva una sola la si manda comunque, perche' il 400 del
 * backend e' la risposta giusta a una finestra a meta' — silenziosamente
 * ripiegare su `period` nasconderebbe il difetto al chiamante.
 */
function costWindowParams(w: CostWindow): URLSearchParams {
  const qs = new URLSearchParams();
  if (w.dateFrom || w.dateTo) {
    if (w.dateFrom) qs.set("date_from", w.dateFrom);
    if (w.dateTo) qs.set("date_to", w.dateTo);
  } else {
    qs.set("period", w.period ?? "30d");
  }
  if (w.tenantId) qs.set("tenant_id", w.tenantId);
  if (w.flow) qs.set("flow", w.flow);
  return qs;
}

export async function adminCostSummary(w: CostWindow = {}): Promise<CostSummary> {
  return apiFetch(`/api/v1/admin/costs/summary?${costWindowParams(w)}`);
}

export async function adminCostsByTenant(
  w: CostWindow = {},
): Promise<CostWindowEcho & { tenants: CostByTenant[] }> {
  return apiFetch(`/api/v1/admin/costs/by-tenant?${costWindowParams(w)}`);
}

export async function adminCostsByType(
  w: CostWindow = {},
): Promise<CostWindowEcho & { types: CostByType[] }> {
  return apiFetch(`/api/v1/admin/costs/by-type?${costWindowParams(w)}`);
}

export async function adminCostsByStep(
  w: CostWindow = {},
): Promise<CostWindowEcho & { steps: CostByStep[] }> {
  return apiFetch(`/api/v1/admin/costs/by-step?${costWindowParams(w)}`);
}

/**
 * Serie temporale con bucket adattivi. `granularity` di default e' `auto`:
 * la risolve il backend sulla larghezza della finestra (<=2 giorni -> ore,
 * 3-92 -> giorni, 93-366 -> settimane, oltre l'anno -> mesi) e la risposta la
 * riporta risolta.
 * Un'unita' troppo fine per la finestra e' un 400, non un troncamento
 * (`hour` oltre 7 giorni, `day` oltre 400, `week` oltre 1098): usa
 * `granularityOptions()` (`lib/costBuckets`) per non offrirla.
 */
export async function adminCostsTimeseries(
  w: CostWindow & { granularity?: CostGranularity | "auto" } = {},
): Promise<CostTimeseries> {
  const qs = costWindowParams(w);
  if (w.granularity) qs.set("granularity", w.granularity);
  return apiFetch(`/api/v1/admin/costs/timeseries?${qs}`);
}

/**
 * Serie a giorni, forma storica: NON riempie i buchi (i giorni senza chiamate
 * mancano) ed e' rimasta per compatibilita'. Per il grafico si usa
 * `adminCostsTimeseries`, che i bucket li riempie.
 */
export async function adminCostsDaily(
  w: CostWindow = {},
): Promise<CostWindowEcho & { days: CostDaily[] }> {
  return apiFetch(`/api/v1/admin/costs/daily?${costWindowParams(w)}`);
}

/**
 * Classifica dei ticket piu' costosi della finestra — il verso opposto di
 * `adminCostsByRequest`: da un costo aggregato si scende al ticket che l'ha
 * generato, e da li' al suo dettaglio.
 *
 * `limit` e' un tetto lato server (1-100): la classifica si calcola in SQL con
 * ORDER BY + LIMIT, quindi chiedere "tutti" non e' un'opzione — la coda lunga
 * di una finestra ampia sono migliaia di pratiche da pochi centesimi.
 */
export async function adminCostsTopRequests(
  w: CostWindow & { limit?: number } = {},
): Promise<CostTopRequestsResponse> {
  const qs = costWindowParams(w);
  if (w.limit != null) qs.set("limit", String(w.limit));
  return apiFetch(`/api/v1/admin/costs/top-requests?${qs}`);
}

export async function adminCostsByRequest(
  requestId: string,
): Promise<CostByRequestResponse> {
  return apiFetch(`/api/v1/admin/costs/by-request/${requestId}`);
}

// Azure Data Factory runs (admin)
export interface AdfRun {
  id: string;
  tenant_id: string;
  pipeline_name: string;
  triggered_by: string;
  started_at: string;
  finished_at: string | null;
  duration_s: number | null;
  status: string; // 'Queued'|'InProgress'|'Succeeded'|'Failed'|'Cancelled'|'TriggerFailed'|'TimedOut'
  error_message: string | null;
  sync_status: string | null; // 'skipped'|'success'|'failed'|null
  sync_started_at: string | null;
  sync_duration_s: number | null;
  rows_changed: Record<string, { upsert?: number; delete?: number; errors?: number }>;
  sync_error: string | null;
  metadata: Record<string, unknown>;
}

export async function adminAdfRunsList(
  limit = 50,
  offset = 0,
): Promise<{ items: AdfRun[]; total: number; limit: number; offset: number }> {
  return apiFetch(`/api/v1/admin/adf-runs?limit=${limit}&offset=${offset}`);
}

export async function adminAdfRunGet(runId: string): Promise<AdfRun> {
  return apiFetch(`/api/v1/admin/adf-runs/${runId}`);
}

export async function adminAdfRunRefresh(): Promise<{ status: string; triggered_by: string }> {
  return apiFetch(`/api/v1/admin/adf-runs/refresh`, { method: "POST" });
}

// Email batch runs (admin + customer admin)
export interface BatchRun {
  id: string;
  triggered_by: string;
  started_at: string;
  finished_at: string | null;
  duration_s: number | null;
  emails_found: number;
  processed: number;
  skipped_dedup: number;
  discarded: number;
  errors: number;
  status: string; // 'running' | 'succeeded' | 'failed'
  error_msg: string | null;
  breakdown: { rdi: number; allarme: number; hyper: number };
}

export async function adminBatchRunsList(
  page = 1,
  pageSize = 20,
): Promise<{ runs: BatchRun[]; total: number; page: number; page_size: number }> {
  return apiFetch(`/api/v1/admin/batch-runs?page=${page}&page_size=${pageSize}`);
}

export async function adminBatchTrigger(): Promise<{ status: string; triggered_by: string }> {
  return apiFetch(`/api/v1/admin/batch-runs/trigger`, { method: "POST" });
}

/* ─── Deleghe V2 (Acquirente Unico) ─── */

import type {
  DelegaResult,
  DelegaStats,
  FirmaStatus,
  DelegaHistoryItem,
  DelegaCheck,
} from "./types";

function buildAuthOnlyHeaders(): Record<string, string> {
  const headers: Record<string, string> = {};
  const token = _getToken?.();
  if (token) headers["Authorization"] = `Bearer ${token}`;
  const tenantId = _getTenantId?.();
  if (tenantId) headers["X-Tenant-ID"] = tenantId;
  return headers;
}

/** Verifica poteri di firma — step singolo (UI). Invia multipart/form-data. */
export async function verifyDelega(data: {
  delegante_nome: string;
  delegante_cognome: string;
  delegante_email: string;
  delegante_telefono?: string;
  delegante_cf: string;
  delegante_data_nascita: string;
  delegato_nome: string;
  delegato_cognome: string;
  delegato_email: string;
  delegato_telefono: string;
  delegato_cf: string;
  delegato_data_nascita: string;
  attivita: string;
  visura: File;
}): Promise<DelegaResult> {
  const fd = new FormData();
  fd.append("delegante_nome", data.delegante_nome);
  fd.append("delegante_cognome", data.delegante_cognome);
  fd.append("delegante_email", data.delegante_email);
  fd.append("delegante_telefono", data.delegante_telefono ?? "");
  fd.append("delegante_cf", data.delegante_cf);
  fd.append("delegante_data_nascita", data.delegante_data_nascita);
  fd.append("delegato_nome", data.delegato_nome);
  fd.append("delegato_cognome", data.delegato_cognome);
  fd.append("delegato_email", data.delegato_email);
  fd.append("delegato_telefono", data.delegato_telefono);
  fd.append("delegato_cf", data.delegato_cf);
  fd.append("delegato_data_nascita", data.delegato_data_nascita);
  fd.append("attivita", data.attivita);
  fd.append("visura", data.visura);

  const res = await fetch(`${BASE}/api/v1/deleghe/verify`, {
    method: "POST",
    headers: buildAuthOnlyHeaders(),
    body: fd,
  });
  if (res.status === 401) {
    window.dispatchEvent(new CustomEvent("auth:unauthorized"));
    throw new Error("Unauthorized");
  }
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`verifyDelega failed ${res.status}: ${text || res.statusText}`);
  }
  return res.json();
}

/** Genera il PDF della procura speciale per un check già completato con verdetto LEGITTIMATO. */
export async function generaDocumentoDelega(checkId: string): Promise<Blob> {
  const res = await fetch(`${BASE}/api/v1/deleghe/${checkId}/genera-documento`, {
    method: "POST",
    headers: buildAuthOnlyHeaders(),
  });
  if (res.status === 401) {
    window.dispatchEvent(new CustomEvent("auth:unauthorized"));
    throw new Error("Unauthorized");
  }
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`generaDocumento failed ${res.status}: ${text || res.statusText}`);
  }
  return res.blob();
}

export async function getDelegaHistory(
  limit = 50,
  offset = 0,
  verdetto?: string,
): Promise<{ items: DelegaHistoryItem[]; total: number }> {
  const qs = new URLSearchParams({ limit: String(limit), offset: String(offset) });
  if (verdetto) qs.append("verdetto", verdetto);
  return apiFetch(`/api/v1/deleghe/history?${qs}`);
}

export async function getDelegaStats(): Promise<DelegaStats> {
  return apiFetch("/api/v1/deleghe/stats");
}

/** Dettaglio completo di una verifica di delega. */
export async function getDelegaCheck(checkId: string): Promise<DelegaCheck> {
  return apiFetch(`/api/v1/deleghe/${checkId}`);
}

/** Scarica il PDF della procura speciale. Ritorna Blob. */
export async function getDocumentoPdf(checkId: string): Promise<Blob> {
  const headers = buildAuthOnlyHeaders();
  const tenantId = _getTenantId?.();
  if (tenantId) headers["X-Tenant-ID"] = tenantId;
  const res = await fetch(`${BASE}/api/v1/deleghe/${checkId}/documento-pdf`, { headers });
  if (!res.ok) throw new Error(`getDocumentoPdf failed ${res.status}`);
  return res.blob();
}

export async function getDocumentoFirmato(checkId: string): Promise<Blob> {
  const headers = buildAuthOnlyHeaders();
  const tenantId = _getTenantId?.();
  if (tenantId) headers["X-Tenant-ID"] = tenantId;
  const res = await fetch(`${BASE}/api/v1/deleghe/${checkId}/documento-firmato`, { headers });
  if (!res.ok) throw new Error(`getDocumentoFirmato failed ${res.status}`);
  return res.blob();
}

/** Avvia il processo di firma per una delega LEGITTIMATA.
 *  Prerequisiti: verdetto=LEGITTIMATO, documento PDF generato, telefoni delegante+delegato.
 */
export async function avviaFirma(checkId: string): Promise<FirmaStatus> {
  return apiFetch(`/api/v1/deleghe/${checkId}/avvia-firma`, { method: "POST" });
}

/** Polling stato processo di firma. */
export async function getFirmaStatus(checkId: string): Promise<FirmaStatus> {
  return apiFetch(`/api/v1/deleghe/${checkId}/firma-status`);
}

/** Annulla il processo di firma e resetta lo stato (best-effort su TrustyCERT). */
export async function annullaFirma(checkId: string): Promise<void> {
  return apiFetch(`/api/v1/deleghe/${checkId}/firma`, { method: "DELETE" });
}

/** Scarica un allegato (binario) con autenticazione. Ritorna object URL per preview/download. */
export async function fetchAttachmentBlob(
  requestId: string,
  filename: string,
): Promise<{ url: string; contentType: string }> {
  const headers: Record<string, string> = {};
  const token = _getToken?.();
  if (token) headers["Authorization"] = `Bearer ${token}`;
  const tenantId = _getTenantId?.();
  if (tenantId) headers["X-Tenant-ID"] = tenantId;

  const res = await fetch(
    `${BASE}/api/v1/requests/${requestId}/attachments/${encodeURIComponent(filename)}`,
    { headers },
  );
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`Download allegato fallito: ${res.status} ${text || res.statusText}`);
  }
  const blob = await res.blob();
  return { url: URL.createObjectURL(blob), contentType: blob.type };
}

// ── BSC ─────────────────────────────────────────────────────────────────────

export async function listBscPractices(
  filters: {
    status?: string;
    q?: string;
    bl?: string;
    invoice?: string;
    country?: string;
    client?: string;
    origin?: string;
    dox?: string;
    hanja?: string;
    cert?: string;
    route?: string;
    party?: string;
    sort?: string;
    date_from?: string;
    date_to?: string;
    page?: number;
    page_size?: number;
  } = {},
): Promise<BscListResponse> {
  const params = new URLSearchParams({
    page: String(filters.page ?? 1),
    page_size: String(filters.page_size ?? 20),
  });
  if (filters.status && filters.status !== "all") params.set("status", filters.status);
  if (filters.q) params.set("q", filters.q);
  if (filters.bl) params.set("bl", filters.bl);
  if (filters.invoice) params.set("invoice", filters.invoice);
  if (filters.country) params.set("country", filters.country);
  if (filters.client) params.set("client", filters.client);
  if (filters.origin) params.set("origin", filters.origin);
  if (filters.dox) params.set("dox", filters.dox);
  if (filters.hanja) params.set("hanja", filters.hanja);
  if (filters.cert) params.set("cert", filters.cert);
  if (filters.route) params.set("route", filters.route);
  if (filters.party) params.set("party", filters.party);
  if (filters.sort) params.set("sort", filters.sort);
  if (filters.date_from) params.set("date_from", filters.date_from);
  if (filters.date_to) params.set("date_to", filters.date_to);
  return apiFetch(`/api/v1/bsc?${params}`);
}

export async function getBscCount(): Promise<BscCount> {
  return apiFetch(`/api/v1/bsc/count`);
}

/** Segna la pratica come letta (stato condiviso di team). Idempotente:
 *  chiamata all'apertura del dettaglio. */
export async function markBscPracticeRead(id: string): Promise<{ ok: boolean; marked: boolean }> {
  return apiFetch(`/api/v1/bsc/${id}/read`, { method: "POST" });
}

/** Segna come lette TUTTE le pratiche non lette di un tab (o di tutte se
 *  `status` e' omesso/"all") — bottone "segna tutte come lette" sulle pill. */
export async function markAllBscPracticesRead(status?: string): Promise<{ ok: boolean; marked: number }> {
  const params = status && status !== "all" ? `?status=${encodeURIComponent(status)}` : "";
  return apiFetch(`/api/v1/bsc/mark-all-read${params}`, { method: "POST" });
}

/** Forza un controllo immediato della mailbox BSC ("Forza aggiornamento"). */
export async function checkNewBsc(): Promise<{ ok: boolean; checked: number; new: number; error?: string }> {
  return apiFetch(`/api/v1/bsc/check-new`, { method: "POST" });
}

/** Forza un controllo immediato della mailbox RDI ("Forza aggiornamento"). */
export async function checkNewRdi(): Promise<{ ok: boolean; checked: number; new: number; error?: string }> {
  return apiFetch(`/api/v1/requests/check-new`, { method: "POST" });
}

/** Forza un controllo immediato della mailbox DDT ("Forza aggiornamento"). */
export async function checkNewDdt(): Promise<{ ok: boolean; checked: number; new: number; error?: string }> {
  return apiFetch(`/api/v1/ddt/check-new`, { method: "POST" });
}

/** Forza una scansione immediata della cartella sorgente Rinascente (nessuna
 *  mailbox: i file arrivano su disco) — "Forza aggiornamento". */
export async function checkNewRinascente(): Promise<{ ok: boolean; checked: number; new: number; error?: string }> {
  return apiFetch(`/api/v1/rinascente/check-new`, { method: "POST" });
}

/** Anteprima del JSON che verrebbe generato all'approvazione (dry-run, applica
 *  le correzioni correnti). Ritorna la struttura { certificato, opzionali? }. */
export async function bscOutputPreview(
  id: string,
  corrections: Record<string, unknown> = {},
): Promise<Record<string, unknown>> {
  return apiFetch(`/api/v1/bsc/${id}/output-preview`, {
    method: "POST",
    body: JSON.stringify({ corrections }),
  });
}

export async function getBscSuggestions(q: string, limit = 10): Promise<BscSuggestion[]> {
  const params = new URLSearchParams({ q, limit: String(limit) });
  return apiFetch(`/api/v1/bsc/suggest?${params}`);
}

export async function getBscPractice(id: string): Promise<BscPractice> {
  return apiFetch(`/api/v1/bsc/${id}`);
}

/** Scarica il blob PDF per anteprima iframe (come Rinascente). */
export async function fetchBscPracticePdf(practiceId: string, filename: string): Promise<Blob> {
  const headers = buildHeaders();
  const res = await fetch(`${BASE}/api/v1/bsc/${practiceId}/pdf/${encodeURIComponent(filename)}`, { headers });
  if (res.status === 401) {
    window.dispatchEvent(new CustomEvent("auth:unauthorized"));
    throw new Error("Unauthorized");
  }
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`PDF ${res.status}: ${text || res.statusText}`);
  }
  return res.blob();
}

export async function approveBscPractice(
  id: string,
  corrections: Partial<BscData>,
  note: string,
): Promise<{ ok: boolean; output_path: string }> {
  return apiFetch(`/api/v1/bsc/${id}/approve`, {
    method: "POST",
    body: JSON.stringify({ corrections, note }),
  });
}

export async function rejectBscPractice(
  id: string,
  categories: string[],
  note: string,
): Promise<{ ok: boolean }> {
  return apiFetch(`/api/v1/bsc/${id}/reject`, {
    method: "POST",
    body: JSON.stringify({ categories, note }),
  });
}

/** Scarta definitivamente (NON riprocessabile). Differenza vs reject:
 *  reject = rifiutata per qualita' (riprendibile); discard = fuori scope/duplicato. */
export async function discardBscPractice(
  id: string,
  note: string,
): Promise<{ ok: boolean; output_cleanup?: { queue_deleted: number; blobs_deleted: number; errors: string[] } }> {
  return apiFetch(`/api/v1/bsc/${id}/discard`, {
    method: "POST",
    body: JSON.stringify({ note }),
  });
}

export async function reprocessBscPractice(
  id: string,
): Promise<{ ok: boolean; completeness: number; non_null_count: number }> {
  return apiFetch(`/api/v1/bsc/${id}/reprocess`, { method: "POST" });
}

export async function lockBscPractice(id: string): Promise<{ locked_by: string; locked_at: string }> {
  return apiFetch(`/api/v1/bsc/${id}/lock`, { method: "POST" });
}

export async function unlockBscPractice(id: string): Promise<{ ok: boolean }> {
  return apiFetch(`/api/v1/bsc/${id}/lock`, { method: "DELETE" });
}

/* ─── Upload manuale documenti BSC ───
 * Fase 1 (uploadBscDocument): salva il file + OCR + classificazione, non
 * tocca bsc_data. Fase 2 (processBscDocument): esegue esplicitamente
 * l'estrazione e applica i campi — la UI chiede sempre conferma tra le due. */

export async function uploadBscDocument(
  practiceId: string,
  file: File,
  tag?: string,
): Promise<BscUploadDocumentResponse> {
  const fd = new FormData();
  fd.append("file", file);
  if (tag) fd.append("tag", tag);

  const res = await fetch(`${BASE}/api/v1/bsc/${practiceId}/documents`, {
    method: "POST",
    headers: buildAuthOnlyHeaders(),
    body: fd,
  });
  if (res.status === 401) {
    window.dispatchEvent(new CustomEvent("auth:unauthorized"));
    throw new Error("Unauthorized");
  }
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    let detail = text;
    try {
      detail = JSON.parse(text)?.detail ?? text;
    } catch {
      // testo non JSON, usa cosi' com'e'
    }
    throw new Error(detail || `Upload fallito (${res.status})`);
  }
  return res.json();
}

export async function processBscDocument(
  practiceId: string,
  filename: string,
): Promise<BscProcessDocumentResponse> {
  return apiFetch(`/api/v1/bsc/${practiceId}/documents/${encodeURIComponent(filename)}/process`, {
    method: "POST",
  });
}

export async function fetchBscDocumentTags(): Promise<BscDocumentTagsResponse> {
  return apiFetch(`/api/v1/bsc/document-tags`);
}

/** Cancella un documento caricato manualmente. Se era già stato elaborato,
 * il BE fa il revert dei campi che aveva scritto (solo quelli di cui questo
 * documento resta l'unico responsabile) e riporta lo slot BL/Invoice a
 * mancante se era stato lui a completarlo. */
export async function deleteBscDocument(
  practiceId: string,
  filename: string,
): Promise<BscDeleteDocumentResponse> {
  return apiFetch(`/api/v1/bsc/${practiceId}/documents/${encodeURIComponent(filename)}`, {
    method: "DELETE",
  });
}

/* ─── Coda Output BSC ─── */

export async function fetchBscOutput(page = 1, size = 50): Promise<BscOutputListResponse> {
  return apiFetch(`/api/v1/bsc/output?page=${page}&size=${size}`);
}

export async function reopenBscPractice(id: string): Promise<BscReopenResult> {
  return apiFetch(`/api/v1/bsc/output/${encodeURIComponent(id)}`, { method: "DELETE" });
}

export async function clearBscOutput(): Promise<BscClearResult> {
  return apiFetch(`/api/v1/bsc/output`, { method: "DELETE" });
}

/* ─── Statistiche BSC ─── */

export async function fetchBscStats(days = 30): Promise<BscStatsResponse> {
  return apiFetch(`/api/v1/bsc/stats?days=${days}`);
}

/* ─── Configurazione BSC ─── */

export async function fetchBscConfig(): Promise<BscConfig> {
  return apiFetch(`/api/v1/bsc/config`);
}

export async function updateBscConfidence(threshold: number): Promise<{ ok: boolean; confidence_threshold_auto_approve: number }> {
  return apiFetch(`/api/v1/bsc/config/confidence`, {
    method: "PUT",
    body: JSON.stringify({ confidence_threshold_auto_approve: threshold }),
  });
}

export async function fetchBscConfidencePreview(threshold: number, days = 30): Promise<BscConfidencePreview> {
  return apiFetch(`/api/v1/bsc/config/confidence/preview?threshold=${threshold}&days=${days}`);
}

// ── DDT (Documenti di Trasporto) — Casadei ──────────────────────────────

export async function listDdtPractices(
  filters: DdtListFilters = {},
): Promise<DdtListResponse> {
  const params = new URLSearchParams();
  const { page = 1, page_size = 20, sort, status, q, id, numero_ddt, ordine_acquisto, fornitore, pdf_filename, fornitore_pk, document_type, date_from, date_to, had_corrections, corrected_field } = filters;
  params.set("page", String(page));
  params.set("page_size", String(page_size));
  if (sort) params.set("sort", sort);
  if (status) params.set("status", status);
  if (q) params.set("q", q);
  if (id) params.set("id", id);
  if (numero_ddt) params.set("numero_ddt", numero_ddt);
  if (ordine_acquisto) params.set("ordine_acquisto", ordine_acquisto);
  if (fornitore) params.set("fornitore", fornitore);
  if (pdf_filename) params.set("pdf_filename", pdf_filename);
  if (fornitore_pk) params.set("fornitore_pk", fornitore_pk);
  if (document_type) params.set("document_type", document_type);
  if (date_from) params.set("date_from", date_from);
  if (date_to) params.set("date_to", date_to);
  if (had_corrections !== undefined) params.set("had_corrections", String(had_corrections));
  if (corrected_field) params.set("corrected_field", corrected_field);
  return apiFetch(`/api/v1/ddt?${params}`);
}

export async function fetchDdtCount(status?: string): Promise<DdtCountResponse> {
  const qs = status ? `?status=${encodeURIComponent(status)}` : "";
  return apiFetch(`/api/v1/ddt/count${qs}`);
}

export async function fetchDdtSuggestions(q: string, limit = 10): Promise<DdtSuggestion[]> {
  const params = new URLSearchParams({ q, limit: String(limit) });
  const res = await apiFetch<{ items: DdtSuggestion[] }>(`/api/v1/ddt/suggest?${params}`);
  return res.items;
}

/** Lookup fornitore su clienti_fornitori per codice/nome/P.IVA (Item 7, 2026-07-14). */
export async function suggestFornitori(q: string, limit = 10): Promise<FornitoreSuggestion[]> {
  const params = new URLSearchParams({ q, limit: String(limit) });
  const res = await apiFetch<{ items: FornitoreSuggestion[] }>(`/api/v1/ddt/fornitori/suggest?${params}`);
  return res.items;
}

/** Lookup articolo su `articoli` per codice fornitore/interno/descrizione (punti 2+5, 2026-07-14). */
export async function suggestArticoli(q: string, limit = 10): Promise<ArticoloSuggestion[]> {
  const params = new URLSearchParams({ q, limit: String(limit) });
  const res = await apiFetch<{ items: ArticoloSuggestion[] }>(`/api/v1/ddt/articoli/suggest?${params}`);
  return res.items;
}

export async function getDdtPractice(id: string): Promise<DdtPractice> {
  return apiFetch(`/api/v1/ddt/${id}`);
}

/** Genera on-demand un magic-link per il proprio account (solo platform_admin) —
 *  bypassa l'invio email, per login rapido o hand-off a un agente di test. */
export async function generateSelfServiceMagicLink(): Promise<SelfServiceMagicLink> {
  return apiFetch("/api/auth/magic-link/self-service", { method: "POST" });
}

/** Ricerca amministratori condominiali su HubSpot (companies) arricchiti con gli
 *  edifici gestiti localmente — Fase 2 amministratore, 2026-07-15.
 *  L'endpoint legge solo `q` (nessun limite lato client). */
export async function fetchAmministratoreSuggest(q: string): Promise<AmministratoreSuggestion[]> {
  const params = new URLSearchParams({ q });
  const res = await apiFetch<{ items: AmministratoreSuggestion[] }>(`/api/v1/requests/amministratori/suggest?${params}`);
  return res.items;
}

export interface DdtValidation {
  blocking_errors: string[];
  is_duplicate: boolean;
  duplicate_of: string | null;
}

export function validateDdtPractice(id: string, corrections: Record<string, unknown>, signal?: AbortSignal): Promise<DdtValidation> {
  return apiFetch(`/api/v1/ddt/${id}/validate`, {
    method: "POST", body: JSON.stringify({ corrections }), signal,
  });
}

export async function approveDdtPractice(
  id: string,
  corrections: Record<string, unknown>,
  note: string,
  reasons: Record<string, string> = {},
  notaOperatore?: string | null,
): Promise<{ ok: boolean; queue_sent: boolean; numero_ddt: string | null }> {
  const payload: Record<string, unknown> = { corrections, reasons, note };
  if (notaOperatore != null) payload.nota_operatore = notaOperatore;
  return apiFetch(`/api/v1/ddt/${id}/approve`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function rejectDdtPractice(
  id: string,
  categories: string[],
  note: string,
): Promise<{ ok: boolean }> {
  return apiFetch(`/api/v1/ddt/${id}/reject`, {
    method: "POST",
    body: JSON.stringify({ categories, note }),
  });
}

/** Avvia il reprocess in background (Item 6) — ritorna subito con status
 * "reprocessing"; il risultato finale si legge facendo polling su
 * getDdtPractice(id) finché practice.status non è più "reprocessing"
 * (vedi DdtReprocessModal in DdtDetail.tsx). */
export async function reprocessDdtPractice(
  id: string,
): Promise<{ ok: boolean; status: string }> {
  return apiFetch(`/api/v1/ddt/${id}/reprocess`, { method: "POST" });
}

/** Ri-esegue SOLO il match articoli (lookup DB, zero OCR/LLM) sulle righe già
 *  persistite — "Ricontrolla articoli". Bloccato se un sync Azure→locale è
 *  in corso (409). */
export async function rematchDdtArticoli(
  id: string,
): Promise<{ ok: boolean; changed: number; message: string }> {
  return apiFetch(`/api/v1/ddt/${id}/rematch-articoli`, { method: "POST" });
}

export interface DdtLockInfo {
  locked: boolean;
  locked_by?: string;
  operator_name?: string;
  locked_at?: string | null;
  expires_at?: string;
}

export async function lockDdtPractice(id: string): Promise<{ status: number; data: DdtLockInfo }> {
  const res = await apiFetchRaw(`/api/v1/ddt/${id}/lock`, { method: "POST" });
  let data: DdtLockInfo = { locked: false };
  try { data = await res.json(); } catch { /* */ }
  return { status: res.status, data };
}

export async function unlockDdtPractice(id: string): Promise<{ ok: boolean }> {
  return apiFetch(`/api/v1/ddt/${id}/lock`, { method: "DELETE" });
}

export function unlockDdtPracticeBeacon(id: string): void {
  const token = _getToken?.();
  const tenantId = _getTenantId?.();
  const url = `${BASE}/api/v1/ddt/${id}/lock`;
  try {
    const headers: Record<string, string> = { "Content-Type": "application/json" };
    if (token) headers["Authorization"] = `Bearer ${token}`;
    if (tenantId) headers["X-Tenant-ID"] = tenantId;
    fetch(url, { method: "DELETE", headers, keepalive: true }).catch(() => {});
  } catch {
    // best effort
  }
}

/**
 * URL per embed/preview del PDF (per <iframe src={...}>).
 * L'endpoint ritorna `Content-Disposition: inline` + `application/pdf`.
 * NOTA: il browser invia automaticamente i cookie; per il Bearer token usare
 * `getDdtPracticePdfBlob` e creare un object URL.
 */
export function getDdtPracticePdfUrl(id: string): string {
  return `${BASE}/api/v1/ddt/${id}/pdf`;
}

/**
 * Scarica il PDF come Blob, usato sia per preview (object URL su <iframe>/<embed>)
 * sia per download forzato. Aggiunge automaticamente Authorization e X-Tenant-ID.
 */
export async function fetchDdtPracticePdf(id: string): Promise<Blob> {
  const headers = buildHeaders();
  const res = await fetch(`${BASE}/api/v1/ddt/${id}/pdf`, { headers });
  if (res.status === 401) {
    window.dispatchEvent(new CustomEvent("auth:unauthorized"));
    throw new Error("Unauthorized");
  }
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`PDF ${res.status}: ${text || res.statusText}`);
  }
  return res.blob();
}

/** Scarica forzatamente il PDF come attachment (trigger del download del browser). */
export async function downloadDdtPracticePdf(id: string, fallbackFilename?: string): Promise<void> {
  const blob = await fetchDdtPracticePdf(id);
  // Tenta di leggere il filename dall'header, fallback al nome passato
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fallbackFilename || `ddt_${id}.pdf`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Libera memoria dopo un tick
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// ── Rinascente / La Rinascente — fatture estere ────────────────────────────────────

export async function fetchRinascenteInvoices(filters: RinascenteListFilters = {}): Promise<RinascenteInvoiceListResponse> {
  const params = new URLSearchParams();
  const { page = 1, page_size = 20, sort, status, tipo, q, data_da, data_a } = filters;
  params.set("page", String(page));
  params.set("page_size", String(page_size));
  if (sort) params.set("sort", sort);
  if (status) params.set("status", status);
  if (tipo) params.set("tipo", tipo);
  if (q) params.set("q", q);
  if (data_da) params.set("data_da", data_da);
  if (data_a) params.set("data_a", data_a);
  return apiFetch(`/api/v1/rinascente?${params}`);
}

export async function fetchRinascenteCount(
  status?: string,
  dateFrom?: string,
): Promise<RinascenteCountResponse> {
  const params = new URLSearchParams();
  if (status) params.set("status", status);
  if (dateFrom) params.set("date_from", dateFrom);
  const qs = params.toString();
  return apiFetch(`/api/v1/rinascente/count${qs ? `?${qs}` : ""}`);
}

export async function fetchRinascenteSuggestions(q: string, limit = 10): Promise<RinascenteSuggestion[]> {
  const params = new URLSearchParams({ q, limit: String(limit) });
  const res = await apiFetch<{ items: RinascenteSuggestion[] }>(`/api/v1/rinascente/suggest?${params}`);
  return res.items;
}

export async function fetchRinascenteInvoice(id: string): Promise<RinascenteInvoice> {
  return apiFetch(`/api/v1/rinascente/${id}`);
}

/**
 * Email originale della fattura. Nel POC ritorna 404 (campi null nel DB).
 * Ritorna null se l'email non è disponibile, così il dettaglio può omettere
 * il pannello senza mostrare errori all'operatore.
 */
export async function fetchRinascenteEmail(id: string): Promise<RinascenteEmail | null> {
  const res = await apiFetchRaw(`/api/v1/rinascente/${id}/email`);
  if (res.status === 404) return null;
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`Email ${res.status}: ${text || res.statusText}`);
  }
  return res.json();
}

/**
 * URL per embed/preview del PDF (per <iframe src={...}> o <a href download>).
 * L'endpoint ritorna `Content-Disposition: inline` + `application/pdf` leggendo
 * il PDF dal filesystem (source_path). Per l'iframe usare fetchRinascenteInvoicePdf
 * (object URL) in modo da passare il Bearer token.
 */
export function rinascentePdfUrl(id: string): string {
  return `${BASE}/api/v1/rinascente/${id}/pdf`;
}

/** Scarica il PDF come Blob con autenticazione (preview object URL o download). */
export async function fetchRinascenteInvoicePdf(id: string): Promise<Blob> {
  const headers = buildHeaders();
  const res = await fetch(`${BASE}/api/v1/rinascente/${id}/pdf`, { headers });
  if (res.status === 401) {
    window.dispatchEvent(new CustomEvent("auth:unauthorized"));
    throw new Error("Unauthorized");
  }
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`PDF ${res.status}: ${text || res.statusText}`);
  }
  return res.blob();
}

export async function approveRinascenteInvoice(
  id: string,
  corrections: Record<string, unknown>,
  note: string,
): Promise<{ ok: boolean; output_path: string }> {
  return apiFetch(`/api/v1/rinascente/${id}/approve`, {
    method: "POST",
    body: JSON.stringify({ corrections, note }),
  });
}

export async function rejectRinascenteInvoice(
  id: string,
  categories: string[],
  note: string,
): Promise<{ ok: boolean }> {
  return apiFetch(`/api/v1/rinascente/${id}/reject`, {
    method: "POST",
    body: JSON.stringify({ categories, note }),
  });
}

export async function reprocessRinascenteInvoice(
  id: string,
): Promise<{ ok: boolean; completeness: number; is_credit_memo: boolean; numero: string | null; n_righe: number }> {
  return apiFetch(`/api/v1/rinascente/${id}/reprocess`, { method: "POST" });
}

export interface RinascenteLockInfo {
  locked: boolean;
  locked_by?: string;
  locked_by_name?: string;
  locked_at?: string | null;
  expires_at?: string;
}

/**
 * Acquisisce/rinnova il lock (TTL 5 min). Race-safe: il BE usa
 * UPDATE...WHERE...RETURNING e ritorna 423 con body JSON se preso da altri.
 */
export async function lockRinascenteInvoice(id: string): Promise<{ status: number; data: RinascenteLockInfo }> {
  const res = await apiFetchRaw(`/api/v1/rinascente/${id}/lock`, { method: "POST" });
  let data: RinascenteLockInfo = { locked: false };
  try { data = await res.json(); } catch { /* */ }
  return { status: res.status, data };
}

export async function unlockRinascenteInvoice(id: string): Promise<{ ok: boolean }> {
  return apiFetch(`/api/v1/rinascente/${id}/lock`, { method: "DELETE" });
}

/** Rilascio best-effort su unmount/beforeunload via fetch keepalive. */
export function unlockRinascenteInvoiceBeacon(id: string): void {
  const token = _getToken?.();
  const tenantId = _getTenantId?.();
  const url = `${BASE}/api/v1/rinascente/${id}/lock`;
  try {
    const headers: Record<string, string> = { "Content-Type": "application/json" };
    if (token) headers["Authorization"] = `Bearer ${token}`;
    if (tenantId) headers["X-Tenant-ID"] = tenantId;
    fetch(url, { method: "DELETE", headers, keepalive: true }).catch(() => {});
  } catch {
    // best effort
  }
}

/** Heartbeat per mantenere il lock (alias di lockRinascenteInvoice, rinnova il TTL). */
export function heartbeatRinascenteInvoice(id: string): Promise<{ status: number; data: RinascenteLockInfo }> {
  return lockRinascenteInvoice(id);
}

/* ─── Coda Output Rinascente ─── */

export async function fetchRinascenteOutput(page = 1, size = 50): Promise<RinascenteOutputListResponse> {
  return apiFetch(`/api/v1/rinascente/output?page=${page}&size=${size}`);
}

export async function reopenRinascenteInvoice(id: string): Promise<RinascenteReopenResult> {
  return apiFetch(`/api/v1/rinascente/output/${encodeURIComponent(id)}`, { method: "DELETE" });
}

export async function clearRinascenteOutput(): Promise<RinascenteClearResult> {
  return apiFetch(`/api/v1/rinascente/output`, { method: "DELETE" });
}

/** Scarica il PDF originale della fattura (utile in coda output e detail). */
export function rinascentePdfDownloadUrl(id: string): string {
  return `${BASE}/api/v1/rinascente/${id}/pdf`;
}

/* ─── Statistiche Rinascente ─── */

export async function fetchRinascenteStats(days = 30): Promise<RinascenteStatsResponse> {
  return apiFetch(`/api/v1/rinascente/stats?days=${days}`);
}

/* ─── Configurazione Rinascente ─── */

export async function fetchRinascenteConfig(): Promise<RinascenteConfig> {
  return apiFetch(`/api/v1/rinascente/config`);
}

export async function updateRinascenteConfidence(threshold: number): Promise<{ ok: boolean; confidence_threshold_auto_approve: number }> {
  return apiFetch(`/api/v1/rinascente/config/confidence`, {
    method: "PUT",
    body: JSON.stringify({ confidence_threshold_auto_approve: threshold }),
  });
}

export async function fetchRinascenteConfidencePreview(threshold: number, days = 30): Promise<RinascenteConfidencePreview> {
  return apiFetch(`/api/v1/rinascente/config/confidence/preview?threshold=${threshold}&days=${days}`);
}

/* ─── Conversazioni (Agente Conversazionale, H4) ─── */

export async function updateTenantModule(
  tenantId: string,
  moduleName: string,
  enabled: boolean,
): Promise<{ status: string }> {
  return apiFetch(`/api/v1/admin/tenants/${tenantId}/modules`, {
    method: "PATCH",
    body: JSON.stringify({ module: moduleName, enabled }),
  });
}

/* ─── Dashboard "Agente": pratiche generate da conversazione (Pacchetto E) ─── */

/** Stessa `/api/v1/requests` della coda RDI, filtrata su `origin=conversation`
 *  (backend: `incoming_data->metadata->>source = 'conversation'`, scritto da
 *  `materializer.py`) — non tocca `fetchRequests` (usata da `RequestList.tsx`
 *  per la coda RDI classica) per restare sulla propria funzione, come da
 *  proprieta' dei file di questo pacchetto. `channel='test'` e' sempre
 *  escluso lato backend, con o senza `origin`. */
export async function fetchAgentRequests(
  params: {
    status?: string;
    q?: string;
    id?: string;
    date_from?: string;
    date_to?: string;
    confidence_min?: number;
    confidence_max?: number;
    priority?: string;
    sender?: string;
    impianto?: string;
    causale?: string;
    numero_riferimento?: string;
    sort?: string;
    page?: number;
    size?: number;
  } = {},
): Promise<PaginatedResponse<ProcessingRequest>> {
  const qs = new URLSearchParams();
  qs.set("origin", "conversation");
  if (params.status) qs.set("status", params.status);
  if (params.q) qs.set("q", params.q);
  if (params.id) qs.set("id", params.id);
  if (params.date_from) qs.set("date_from", params.date_from);
  if (params.date_to) qs.set("date_to", params.date_to);
  if (params.confidence_min != null) qs.set("confidence_min", String(params.confidence_min));
  if (params.confidence_max != null) qs.set("confidence_max", String(params.confidence_max));
  if (params.priority) qs.set("priority", params.priority);
  if (params.sender) qs.set("sender", params.sender);
  if (params.impianto) qs.set("impianto", params.impianto);
  if (params.causale) qs.set("causale", params.causale);
  if (params.numero_riferimento) qs.set("numero_riferimento", params.numero_riferimento);
  if (params.sort) qs.set("sort", params.sort);
  if (params.page) qs.set("page", String(params.page));
  if (params.size) qs.set("size", String(params.size));
  return apiFetch(`/api/v1/requests?${qs.toString()}`);
}

/** Transcript + decisioni della conversazione che ha materializzato questa
 *  pratica — alimenta la sezione "Conversazione" in Review3.tsx. 404 se la
 *  pratica non ha origine conversazionale. */
export async function fetchConversationByRequest(requestId: string): Promise<ConversationByRequest> {
  return apiFetch(`/api/v1/conversations/by-request/${encodeURIComponent(requestId)}`);
}

/** Pratiche nate da una conversazione sul canale `test` (`/settings/agente/pratiche`):
 *  oggi invisibili in ogni altra lista, perche' `fetchAgentRequests`/`list_requests`
 *  escludono SEMPRE `channel='test'` — giustamente, per non gonfiare la coda
 *  vera con messaggi di prova. Stessa `/api/v1/requests`, con `only_test_channel=true`
 *  al posto di `origin=conversation`: il backend, con questo flag, inverte
 *  l'esclusione e restituisce SOLO le pratiche di test. */
export async function fetchTestChannelRequests(
  params: TestChannelRequestsParams = {},
): Promise<PaginatedResponse<ProcessingRequest>> {
  const qs = new URLSearchParams();
  qs.set("only_test_channel", "true");
  if (params.status) qs.set("status", params.status);
  if (params.date_from) qs.set("date_from", params.date_from);
  if (params.date_to) qs.set("date_to", params.date_to);
  if (params.sort) qs.set("sort", params.sort);
  if (params.page) qs.set("page", String(params.page));
  if (params.size) qs.set("size", String(params.size));
  return apiFetch(`/api/v1/requests?${qs.toString()}`);
}

/* ─── Listino prezzi (run `prezzi-listino`, contratto C3) ─── */

/**
 * Errore strutturato delle API del listino: la pagina deve distinguere il
 * 422 (validazione: prezzo zero, intervallo sovrapposto, campi incoerenti
 * con l'unita') dal 409 (anteprima del ricalcolo stantia) e mostrare il
 * `detail` del backend accanto al form, cosa che la stringa piatta di
 * `apiFetch` ("API 422: {...}") non permette senza riparsarla.
 */
export class AdminPricesApiError extends Error {
  status: number;
  detail: string;
  constructor(status: number, detail: string) {
    super(detail);
    this.name = "AdminPricesApiError";
    this.status = status;
    this.detail = detail;
  }
}

async function adminPricesFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await apiFetchRaw(path, init);
  if (res.ok) return res.json();
  if (res.status === 403) throw new AdminPricesApiError(403, "Solo l'amministratore di piattaforma puo' gestire il listino");
  const text = await res.text().catch(() => "");
  let detail = text || res.statusText || `Errore ${res.status}`;
  try {
    const parsed = JSON.parse(text) as { detail?: unknown };
    if (typeof parsed.detail === "string") detail = parsed.detail;
    else if (Array.isArray(parsed.detail)) {
      // Formato di validazione di FastAPI/Pydantic: [{loc, msg, ...}]
      const parts = (parsed.detail as { msg?: string; loc?: unknown[] }[])
        .map((d) => (d.loc?.length ? `${String(d.loc[d.loc.length - 1])}: ${d.msg ?? ""}` : d.msg ?? ""))
        .filter(Boolean);
      if (parts.length) detail = parts.join("; ");
    }
  } catch { /* corpo non JSON: si mostra il testo com'e' */ }
  throw new AdminPricesApiError(res.status, detail);
}

export async function adminListModelPrices(): Promise<import("./types").ModelPriceSummary[]> {
  return adminPricesFetch("/api/v1/admin/prices/models");
}

export async function adminModelPriceHistory(modelId: string): Promise<import("./types").PriceView[]> {
  return adminPricesFetch(`/api/v1/admin/prices/models/${encodeURIComponent(modelId)}/history`);
}

/** 201 con il prezzo creato; 422 (`AdminPricesApiError.detail`) se rifiutato (A5). */
export async function adminAddModelPrice(modelId: string, body: import("./types").PriceInput): Promise<import("./types").PriceView> {
  return adminPricesFetch(`/api/v1/admin/prices/models/${encodeURIComponent(modelId)}/prices`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

/** Chiudere un prezzo = impostarne `valid_to`; le righe non si modificano mai. */
export async function adminCloseModelPrice(modelId: string, priceId: number, validTo: string): Promise<import("./types").PriceView> {
  return adminPricesFetch(
    `/api/v1/admin/prices/models/${encodeURIComponent(modelId)}/prices/${priceId}/close`,
    { method: "POST", body: JSON.stringify({ valid_to: validTo }) },
  );
}

/** Anteprima del ricalcolo: il DB non cambia. */
export async function adminRecalcPreview(body: import("./types").RecalcRequest): Promise<import("./types").RecalcReportView> {
  return adminPricesFetch("/api/v1/admin/prices/recalc/preview", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

/** Applica il ricalcolo dell'anteprima; 409 se il `fingerprint` e' stantio. */
export async function adminRecalcApply(body: import("./types").RecalcRequest & { fingerprint: string }): Promise<import("./types").RecalcApplyView> {
  return adminPricesFetch("/api/v1/admin/prices/recalc/apply", {
    method: "POST",
    body: JSON.stringify(body),
  });
}
